import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ROTULO_CATEGORIA_DOCUMENTO, type CategoriaDocumento } from '@reducee/contracts';
import { useEffect, useId, useState } from 'react';

import { chamarApi, ErroDaApi } from '../api/cliente.ts';

export type DocumentoDoContato = {
  id: string;
  nome: string;
  categoria: CategoriaDocumento;
  tipoMime: string | null;
  tamanhoBytes: number;
  enviadoEm: string;
  beneficiario: { id: string; nome: string } | null;
};

/**
 * Em que momento o documento entra.
 *
 * A pasta continua sendo uma só — a do Cliente —, mas a Implantação e a
 * Movimentação listam e gravam apenas os seus, e com a categoria do próprio
 * contexto em vez do `CLIENTE` que todo envio carregava.
 */
export type VinculoDoDocumento = {
  categoria: CategoriaDocumento;
  implantacaoId?: string;
  movimentacaoId?: string;
};

/** Vidas cobertas pelo Contrato, para dizer de quem é cada documento. */
export type VidaDoContrato = { id: string; nome: string };

const EXTENSOES_ACEITAS =
  '.pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.xlsx,.xls,.csv,.docx,.txt';
const LIMITE_BYTES = 10 * 1024 * 1024;

/**
 * Só estes viram miniatura. HEIC e HEIF são aceitos no envio porque é o que o
 * iPhone grava, mas nenhum navegador os desenha: miniatura deles seria moldura
 * vazia, que engana mais do que ajuda.
 */
const TIPOS_COM_PREVIA = new Set(['image/jpeg', 'image/png', 'image/webp']);

function temPrevia(tipoMime: string | null | undefined): boolean {
  return typeof tipoMime === 'string' && TIPOS_COM_PREVIA.has(tipoMime);
}

/** Lista e, na Oportunidade, envia Documentos pelo mesmo caminho da Visão 360º. */
export function DocumentosDoContato({
  contatoId,
  permitirEnvio = false,
  documentosIniciais,
  compacto = false,
  vinculo = { categoria: 'CLIENTE' },
  vidas = [],
  vidaPadrao,
  titulo = 'Documentos',
  teste,
}: {
  contatoId: string;
  permitirEnvio?: boolean;
  documentosIniciais?: DocumentoDoContato[];
  compacto?: boolean;
  /** Momento a que o documento pertence. Sem ele, o arquivo é da pasta do Cliente. */
  vinculo?: VinculoDoDocumento;
  /** Quando o Contrato cobre mais de uma vida, o envio pergunta de quem é. */
  vidas?: VidaDoContrato[];
  vidaPadrao?: string;
  titulo?: string;
  teste?: string;
}): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);
  const [semMiniatura, setSemMiniatura] = useState<ReadonlySet<string>>(new Set());

  // O filtro entra na chave: a pasta do Cliente e a lista de uma Implantação
  // são consultas diferentes, e guardar as duas sob a mesma chave faria a
  // segunda sobrescrever a primeira no cache.
  const filtro = new URLSearchParams(
    Object.entries({
      movimentacaoId: vinculo.movimentacaoId,
      implantacaoId: vinculo.implantacaoId,
    }).filter((par): par is [string, string] => Boolean(par[1])),
  ).toString();

  const documentos = useQuery({
    queryKey: ['documentos', contatoId, filtro],
    queryFn: () =>
      chamarApi<DocumentoDoContato[]>(
        `/contatos/${contatoId}/documentos${filtro ? `?${filtro}` : ''}`,
      ),
    initialData: documentosIniciais,
  });

  const invalidarConsultas = (): Promise<void> =>
    invalidarDocumentos(clienteDeConsultas, contatoId);

  const excluir = useMutation({
    mutationFn: (documentoId: string) =>
      chamarApi<void>(`/documentos/${documentoId}`, { metodo: 'DELETE' }),
    onMutate: () => setErro(null),
    onSuccess: invalidarConsultas,
    onError: (causa: unknown) => {
      setErro(causa instanceof ErroDaApi ? causa.message : 'Não foi possível excluir o Documento.');
    },
  });

  const lista = documentos.data ?? [];

  return (
    <div
      data-teste={teste ?? (compacto ? 'oportunidade-documentos' : 'visao-360-documentos')}
      className={
        compacto
          ? 'rounded-[9px] border border-linha bg-marinho-suave p-3 md:col-span-2'
          : 'rounded-xl bg-superficie p-5 ring-1 ring-linha'
      }
    >
      <h2 className="font-semibold text-marinho">{titulo}</h2>

      {permitirEnvio ? (
        <FormularioDeEnvio
          contatoId={contatoId}
          vinculo={vinculo}
          vidas={vidas}
          {...(vidaPadrao ? { vidaPadrao } : {})}
        />
      ) : null}

      {/*
        O erro da exclusão vive aqui, e não dentro do formulário: na Oportunidade
        sem envio permitido ele não teria onde aparecer, e a falha passaria calada.
      */}
      {erro ? (
        <p role="alert" className="mt-2 rounded-lg bg-erro/10 px-3 py-2 text-sm text-erro">
          {erro}
        </p>
      ) : null}

      {documentos.isPending ? <p className="mt-2 text-sm text-texto-suave">Carregando…</p> : null}
      {documentos.isError ? (
        <p role="alert" className="mt-2 rounded-lg bg-erro/10 px-3 py-2 text-sm text-erro">
          Não foi possível carregar os Documentos.
        </p>
      ) : null}
      {!documentos.isPending && !documentos.isError && lista.length === 0 ? (
        <p className="mt-2 text-sm text-texto-suave">Nenhum documento anexado.</p>
      ) : null}
      {!documentos.isError && lista.length > 0 ? (
        <ul className="mt-3 space-y-2">
          {lista.map((documento) => (
            <li key={documento.id} className="rounded-lg bg-marinho-suave px-3 py-2 text-sm">
              <div className="flex gap-2.5">
                {/*
                  A miniatura é o próprio arquivo: não há rota de thumbnail, e
                  cada uma baixa o objeto inteiro. `loading="lazy"` segura as que
                  estão fora da tela, e `alt` vazio evita o leitor de tela repetir
                  o nome que já está escrito ao lado.

                  Quem não carrega sai da tela em vez de virar moldura vazia: a
                  caixa de 44px com borda e nada dentro parece Documento
                  corrompido, e o arquivo pode estar ótimo — o objeto é que pode
                  ter sumido do bucket, ou a sessão expirado no meio.
                */}
                {temPrevia(documento.tipoMime) && !semMiniatura.has(documento.id) ? (
                  <img
                    src={`/api/documentos/${documento.id}`}
                    alt=""
                    loading="lazy"
                    onError={() =>
                      setSemMiniatura((anteriores) => new Set(anteriores).add(documento.id))
                    }
                    className="h-11 w-11 shrink-0 rounded-md object-cover ring-1 ring-linha"
                  />
                ) : null}
                <div className="min-w-0 flex-1">
                  <div className="flex justify-between gap-2">
                    <a
                      href={`/api/documentos/${documento.id}`}
                      className="truncate font-medium underline decoration-dotted"
                    >
                      {documento.nome}
                    </a>
                    <span className="shrink-0 text-xs text-texto-suave">
                      {formatarTamanho(documento.tamanhoBytes)}
                    </span>
                  </div>
                  <p className="text-xs text-texto-suave">
                    {[
                      documento.beneficiario?.nome,
                      ROTULO_CATEGORIA_DOCUMENTO[documento.categoria],
                      new Date(documento.enviadoEm).toLocaleDateString('pt-BR'),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
              </div>
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  aria-label={`Excluir ${documento.nome}`}
                  disabled={excluir.isPending}
                  onClick={() => {
                    if (window.confirm(`Excluir o Documento “${documento.nome}”?`)) {
                      excluir.mutate(documento.id);
                    }
                  }}
                  className="rounded-[9px] border border-erro/40 px-2.5 py-1 text-xs font-[750] text-erro transition hover:bg-erro/10 disabled:opacity-60"
                >
                  Excluir
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * O envio de um documento: escolher, dizer de quem é, mandar.
 *
 * Separado da lista porque são dois assuntos com estados próprios — o arquivo
 * ainda não enviado e a prévia dele não têm nada a ver com o que já está no
 * bucket, e juntá-los fazia um componente que ninguém conseguia ler inteiro.
 */
function FormularioDeEnvio({
  contatoId,
  vinculo,
  vidas,
  vidaPadrao,
}: {
  contatoId: string;
  vinculo: VinculoDoDocumento;
  vidas: VidaDoContrato[];
  vidaPadrao?: string;
}): React.JSX.Element {
  const campoId = useId();
  const clienteDeConsultas = useQueryClient();
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [previa, setPrevia] = useState<string | null>(null);
  const [vidaEscolhida, escolherVida] = useState<string | null>(null);
  const vidaId = vidaEscolhida ?? vidaPadrao ?? '';

  // A prévia do que ainda não foi enviado sai do arquivo na máquina de quem
  // envia, sem passar pela API. O endereço é revogado ao trocar de arquivo:
  // sem isso, cada escolha deixa um blob preso na memória da aba.
  useEffect(() => {
    if (!arquivo || !temPrevia(arquivo.type)) {
      setPrevia(null);
      return;
    }
    const endereco = URL.createObjectURL(arquivo);
    setPrevia(endereco);
    return () => URL.revokeObjectURL(endereco);
  }, [arquivo]);

  /** Devolve o campo ao estado vazio, inclusive o valor que o navegador guarda. */
  const limparEscolha = (): void => {
    setArquivo(null);
    setErro(null);
    const campo = document.getElementById(campoId);
    if (campo instanceof HTMLInputElement) campo.value = '';
  };

  const enviar = useMutation({
    mutationFn: async (selecionado: File) => {
      if (selecionado.size > LIMITE_BYTES) {
        throw new ErroDaApi(413, 'Arquivo acima do limite de 10 MiB.', null);
      }
      const corpo = new FormData();
      corpo.set('arquivo', selecionado);

      const parametros = new URLSearchParams({ categoria: vinculo.categoria });
      if (vinculo.movimentacaoId) parametros.set('movimentacaoId', vinculo.movimentacaoId);
      if (vinculo.implantacaoId) parametros.set('implantacaoId', vinculo.implantacaoId);
      if (vidaId) parametros.set('beneficiarioId', vidaId);

      return chamarApi<DocumentoDoContato>(
        `/contatos/${contatoId}/documentos?${parametros.toString()}`,
        { metodo: 'POST', corpo },
      );
    },
    onSuccess: async () => {
      limparEscolha();
      await invalidarDocumentos(clienteDeConsultas, contatoId);
    },
    onError: (causa: unknown) => {
      setErro(causa instanceof ErroDaApi ? causa.message : 'Não foi possível enviar o Documento.');
    },
  });

  return (
        <div className="mt-3 rounded-lg bg-superficie p-3 ring-1 ring-linha">
          <label htmlFor={campoId} className="text-[11px] font-extrabold text-texto-suave">
            Anexar documento
          </label>
          {/*
            O controle nativo desenha o próprio botão e escreve nele no idioma do
            navegador de quem usa — "Choose File", "Escolher ficheiro". Some de
            vista com `sr-only`, e não com `hidden`: assim continua alcançável
            por teclado e com nome na árvore de acessibilidade.
          */}
          <input
            id={campoId}
            type="file"
            accept={EXTENSOES_ACEITAS}
            onChange={(evento) => {
              setArquivo(evento.target.files?.[0] ?? null);
              setErro(null);
            }}
            className="sr-only"
          />
          <div className="mt-1.25 flex flex-wrap items-center gap-2">
            <label
              htmlFor={campoId}
              className="cursor-pointer rounded-[9px] border border-linha bg-marinho-suave px-3 py-1.5 text-[13px] font-[750] text-marinho transition hover:bg-linha"
            >
              Escolher arquivo
            </label>
            {/*
              `min-w-40` é o que faz o nome descer para a linha de baixo quando
              não cabe ao lado do botão — o que acontece na janela da
              Oportunidade, onde o bloco é mais estreito que na Visão 360º. Sem
              isso, o corte comia até o texto de estado vazio. Truncar continua
              valendo para nome comprido, que é onde o corte tem serventia.
            */}
            <span className="min-w-40 flex-1 truncate text-sm text-texto-suave">
              {arquivo ? arquivo.name : 'Nenhum arquivo escolhido'}
            </span>
            {arquivo ? (
              <button
                type="button"
                onClick={limparEscolha}
                aria-label={`Remover ${arquivo.name} da seleção`}
                className="rounded-[9px] border border-linha px-2.5 py-1 text-xs font-[750] text-texto-suave transition hover:bg-marinho-suave"
              >
                Remover
              </button>
            ) : null}
          </div>
          {previa ? (
            <img
              src={previa}
              alt=""
              className="mt-2 max-h-32 rounded-lg object-contain ring-1 ring-linha"
            />
          ) : null}
          {/*
            De quem é o documento. Só aparece quando o Contrato cobre mais de
            uma vida: com uma só, perguntar seria pedir à pessoa que repita o
            que a tela já sabe. "Do contrato" é a saída para o que não é de
            ninguém em particular — contrato assinado, boleto, proposta.
          */}
          {vidas.length > 1 ? (
            <div className="mt-2">
              <label htmlFor={`${campoId}-vida`} className="text-[11px] font-extrabold text-texto-suave">
                De quem é este documento
              </label>
              <select
                id={`${campoId}-vida`}
                value={vidaId}
                onChange={(evento) => escolherVida(evento.target.value)}
                className="mt-1.25 w-full rounded-[9px] border border-linha bg-superficie px-2.75 py-2 text-sm outline-none focus:border-marinho-claro"
              >
                <option value="">Do contrato, não de uma pessoa</option>
                {vidas.map((vida) => (
                  <option key={vida.id} value={vida.id}>
                    {vida.nome}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-texto-suave">
              PDF, imagens, Excel, CSV, Word ou texto · até 10 MiB.
            </p>
            <button
              type="button"
              disabled={!arquivo || enviar.isPending}
              onClick={() => arquivo && enviar.mutate(arquivo)}
              className="rounded-[9px] bg-marinho px-3 py-1.5 text-[13px] font-[750] text-white transition hover:bg-marinho-claro disabled:opacity-60"
            >
              {enviar.isPending ? 'Enviando…' : 'Enviar documento'}
            </button>
          </div>
          {erro ? (
            <p role="alert" className="mt-2 rounded-lg bg-erro/10 px-3 py-2 text-sm text-erro">
              {erro}
            </p>
          ) : null}
        </div>
  );
}

/**
 * O que precisa ser relido depois de um documento entrar ou sair.
 *
 * São quatro listas e nenhuma depende da outra: a pasta do Contato, a Visão
 * 360º que abre com ela pronta, e as contagens de documento que Movimentações
 * e Implantação mostram em coluna.
 */
async function invalidarDocumentos(cliente: QueryClient, contatoId: string): Promise<void> {
  await Promise.all([
    cliente.invalidateQueries({ queryKey: ['documentos', contatoId] }),
    cliente.invalidateQueries({ queryKey: ['visao-360', contatoId] }),
    cliente.invalidateQueries({ queryKey: ['movimentacoes'] }),
    cliente.invalidateQueries({ queryKey: ['implantacoes'] }),
  ]);
}

/** Tamanho legível: quem confere anexo pensa em KB e MB, não em bytes. */
function formatarTamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
